import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { useEffect, useState, useContext, cloneElement } from "react";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import { byAttributeName, bySystemAttributeDesc } from "../utils";
import ActionTable from "../ActionTable/ActionTable";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import React from "react";
import { getAttributes } from "../../../API/Attribute/Attribute";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";

function SpecificHeading() {

    const formProperties = componentPropertiesMap.SpecificHeading;
    const { user, subscription } = useContext(GlobalContext).state;
    // hooks

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");
    const project = usePropsStoreValue("project");
    const advancedMode = usePropsStoreValue("advancedMode");
    const isInternalUser = useInternalUserCheck(user.email);
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent()
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    const [properties, setProperties] = useComponentProperties();
    const [{ isGeneralPropertiesChanged }, setChangeProperty] = useChangeBooleans();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();

    const { t, ready: tReady } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/specific_heading"
    ]);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (selectedComponent) {
            getData();
        }
    }, [])

    useEffect(() => {
        if (
            tReady &&
            fetchedValues.attributes &&
            fetchedValues.attributes.length > 0 &&
            fetchedValues.attributes[0].id !== ""
        ) {
            updateGeneralFetchedValue([
                { attributeName: t("attributes.select_attribute", { ns: "display/common" }), id: "" },
                ...fetchedValues.attributes
            ]);
        }
    }, [tReady, fetchedValues.attributes])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (!loading) {
            setPropertiesUsingAttributes(fetchedValues.attributes)
        }
    }, [selectedComponent, selectedPage])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    // state
    const [selectedTab, setSelectedTab] = useState(0);

    // functions
    async function getData() {
        const attributes = await getAttributes(entityId);
        const sortedAttributes = attributes.sort(byAttributeName).sort(bySystemAttributeDesc);
        const filteredAttributes = sortedAttributes.filter(attribute => (
            attribute.attributeType !== "Image" &&
            !(attribute.attributeType === "Mirror" && ["Image", "Html"].includes(attribute.attribute.attributeType))
        ))
        setPropertiesUsingAttributes(filteredAttributes);
        updateGeneralFetchedValue("attributes", filteredAttributes);
        setLoading(false);
    }

    function setPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperties(property, selectedComponent[property])
            }
        }
        const firstAttribute = attributes.find(attr => attr._id === selectedComponent.firstAttributeId);
        const secondAttribute = attributes.find(attr => attr._id === selectedComponent.secondAttributeId);
        setProperties("firstAttribute", firstAttribute || { _id: "" });
        setProperties("secondAttribute", secondAttribute || { _id: "" });
    }

    // handlers
    function handleTabSelect(e) {
        setSelectedTab(e.selected);
    };

    function handleInputChange(e) {
        setProperties(e.target.name, e.target.value);
    };

    function setLabelTranslation(data) {
        setProperties("languages", data);
        setShowLabelTranslationDialog(false);
    }

    function removeSecondAttribute() {
        setProperties("secondAttribute", null);
    }

    function handleCheckboxChange(e) {
        setProperties(e.target.name, e.target.value);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined &&
                selectedComponent[property] !== properties[property]
            ) {
                isChanged = true;
                break;
            }
        }
        if (properties.firstAttribute && properties.firstAttribute._id !== "" && properties.firstAttribute._id !== selectedComponent.firstAttributeId) {
            isChanged = true;
        }
        if (properties.secondAttribute && properties.secondAttribute._id !== "" && properties.secondAttribute._id !== selectedComponent.secondAttributeId) {
            isChanged = true;
        } else if (properties.secondAttribute === null && selectedComponent.secondAttributeId !== "") {
            isChanged = true;
        }
        if (properties.languages && isLanguagesChanged(properties.languages, selectedComponent.languages)) {
            isChanged = true;
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged)
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    function isLanguagesChanged(languages, defaultLanguages) {
        const keys = Object.keys(languages);
        for (const key of keys) {
            if (languages[key] === undefined || defaultLanguages[key] === undefined) {
                debugger;
            }
            if (
                languages[key].label !== defaultLanguages[key].label ||
                languages[key].tooltip !== defaultLanguages[key].tooltip
            ) {
                return true;
            }
        }
        return false;
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
    }

    function getPropertyToShowInHeadingAttributes() {
        const attributes = fetchedValues.attributes;
        if (!attributes) {
            return [];
        }
        const filteredAttributes = attributes.filter(attribute => attribute.attributeType !== "Html" && attribute.attributeType !== "Attachment");
        return filteredAttributes;
    }

    if (loading) {
        return <ComponentFormSkeleton />
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={selectedTab} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                <div className="form-label">
                                    {t("form_labels.label", { ns: "display/components/specific_heading" })}
                                </div>
                                <input
                                    disabled={selectedComponent.hidden}
                                    placeholder="Enter Label"
                                    className="form-control w-50"
                                    name="label"
                                    onChange={handleInputChange}
                                    maxLength={256}
                                    value={properties.label}
                                />
                                {subscription.numOfLanguages > 1 &&
                                    <div className="col-12">
                                        <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                                            setShowLabelTranslationDialog(true)
                                        }}>
                                            <span className="text-primary">
                                                {t("form_labels.label_translation", { ns: "display/components/specific_heading" })}
                                            </span>
                                        </div>
                                    </div>
                                }
                                {showLabelTranslationDialog && (
                                    <GenericTranslationDialog
                                        onClose={() => { setShowLabelTranslationDialog(false) }}
                                        onSave={setLabelTranslation}
                                        languages={properties.languages}
                                        project={project}
                                        fields={["label"]}
                                        type={t("form_labels.heading", { ns: "display/components/specific_heading" })}
                                    />
                                )}
                                {
                                    accessedFromDesigner &&
                                    <Checkbox
                                        name="showEntityIcon"
                                        disabled={selectedComponent.hidden}
                                        checked={properties.showEntityIcon}
                                        onChange={handleInputChange}
                                        label={t("form_labels.entity_icon", { ns: "display/components/specific_heading" })}
                                        className="my-1 d-block"
                                    />
                                }
                                <div className="mt-3 mb-2 font-size-0 font-weight-2">
                                    {t("form_labels.property_to_show_in_heading", { ns: "display/components/specific_heading" })}
                                </div>
                                {properties.isDuplicateName &&
                                    <span className="mx-1 text-danger">
                                        <i className="mx-1 fas fa-exclamation-circle"></i>
                                        {t("errors.attribute_selected_twice", { ns: "display/components/specific_heading" })}
                                    </span>
                                }
                                <div className="form-label">
                                    {t("form_labels.property_1", { ns: "display/components/specific_heading" })}
                                </div>
                                <DropDownList
                                    name="firstAttribute"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    className="form-control-custom w-50"
                                    textField="attributeLabel"
                                    value={properties.firstAttribute}
                                    data={getPropertyToShowInHeadingAttributes()}
                                    dataItemKey="_id"
                                />
                                <div className="form-label">
                                    {t("form_labels.property_2", { ns: "display/components/specific_heading" })}
                                </div>
                                <DropDownList
                                    name="secondAttribute"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    valueRender={(element, value) => (
                                        <ValueRender
                                            element={element}
                                            value={value}
                                            onRemove={removeSecondAttribute}
                                        />
                                    )}
                                    className="form-control-custom w-50"
                                    textField="attributeLabel"
                                    value={properties.secondAttribute}
                                    data={getPropertyToShowInHeadingAttributes()}
                                    dataItemKey="_id"
                                />
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

const ValueRender = ({ element, value, onRemove }) => {
    if (!value) {
        return element;
    }

    function handleRemove(e) {
        e.stopPropagation();
        onRemove(e);
    }

    const children = [
        <div className="d-flex justify-content-between align-items-center w-100">
            <span className="text-wrap" key={2}>&nbsp; {element.props.children}</span>
            <span key={1} className="fa-solid fa-xmark text-danger" onClick={handleRemove}></span>
        </div>
    ];

    return cloneElement(element, { ...element.props }, children);
};

export default SpecificHeading;